import {
  API_BASE_URL,
  flyersContainer,
  stickyHeading,
  navOptions,
  dealModal,
  dealModalImage,
  dealModalMerchant,
  dealModalProduct,
  dealModalOfferPrice,
  dealModalRegularPrice,
  dealModalWeight,
  dealModalExpiryDate,
  dealModalFavoriteBtn,
  dealModalPageNo,
  db,
  AppState
} from './globals.js';
import {
  formatMerchantName,
  formatDate,
  setupCardImages,
  showFlyerGallery,
  setupAutoScrollImages,
  setupHeroMarquee
} from './ui.js';

// ============================================
// OFFLINE CACHE DATABASE (IndexedDB)
// ============================================
export async function getOfflineDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("WalletoDB", 1);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains("api_cache")) {
        db.createObjectStore("api_cache", { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getCachedData(key) {
  try {
    const db = await getOfflineDB();
    return new Promise((resolve) => {
      const tx = db.transaction("api_cache", "readonly");
      const store = tx.objectStore("api_cache");
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result ? req.result.data : null);
      req.onerror = () => resolve(null);
    });
  } catch (e) { return null; }
}

export async function setCachedData(key, data) {
  try {
    const db = await getOfflineDB();
    const tx = db.transaction("api_cache", "readwrite");
    tx.objectStore("api_cache").put({ key, data });
  } catch (e) { console.error("Cache save failed", e); }
}

export async function clearAllCachedData() {
  try {
    const db = await getOfflineDB();
    const tx = db.transaction("api_cache", "readwrite");
    tx.objectStore("api_cache").clear();
    console.log("Offline cache cleared for security.");
  } catch (e) { 
    console.error("Failed to clear cache", e); 
  }
}

// ============================================
// FLYERS FUNCTIONS
// ============================================

const capitalizeFirst = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export function preloadFlyerImages() {
  const allUrls = new Set();
  AppState.allFlyers.forEach((flyer) => {
    if (flyer.FlyerImageUrl) allUrls.add(flyer.FlyerImageUrl);
  });
  allUrls.forEach((url) => {
    const img = new Image();
    img.src = url;
  });
}

export async function fetchGeneralFlyers() {
  try {
    const country = capitalizeFirst(AppState.selectedCountryName || "Canada");
    const city = capitalizeFirst(AppState.mapped_city || AppState.city || "Milton");

    const response = await fetch(
      `${API_BASE_URL}/flyers?country=${encodeURIComponent(country)}&city=${encodeURIComponent(city)}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      },
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data.flyers || [];
  } catch (error) {
    console.error("Error fetching general flyers:", error);
    throw error;
  }
}

export async function fetchMerchantFlyers(merchantName) {
  try {
    const country = capitalizeFirst(AppState.selectedCountryName || "Canada");
    const city = capitalizeFirst(AppState.mapped_city || AppState.city || "Milton");

    const response = await fetch(
      `${API_BASE_URL}/flyersMerchant?merchant=${encodeURIComponent(merchantName)}&country=${encodeURIComponent(country)}&city=${encodeURIComponent(city)}`,
      { method: "GET", headers: { "Content-Type": "application/json" } },
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return data.flyers || [];
  } catch (error) {
    console.error(`Error fetching flyers for ${merchantName}:`, error);
    throw error;
  }
}

export async function loadFlyers() {
  const country = capitalizeFirst(AppState.selectedCountryName || "Canada");
  let city = AppState.mapped_city || AppState.city;

  if (!city) {
    try {
      const savedLoc = JSON.parse(localStorage.getItem("walleto_location") || "{}");
      city = savedLoc.mapped_city || savedLoc.mappedCity || savedLoc.city || "Milton";
    } catch (e) {
      city = "Milton";
    }
  }
  city = capitalizeFirst(city);

  const cacheKey = `flyers_${country}_${city}`;

  // 1. INSTANT MOUNT
  const cachedFlyers = await getCachedData(cacheKey);
  if (cachedFlyers) {
    AppState.allFlyers = cachedFlyers.allFlyers;
    AppState.merchantFlyersMap = new Map(cachedFlyers.merchantMapEntries);
    displayFlyers(cachedFlyers.visibleGeneralFlyers);
    setupCardImages();
    setupHeroMarquee();
    
    // Remove the loading overlay immediately since we have cached data
    const loadingOverlay = flyersContainer.querySelector(".loading-overlay");
    if (loadingOverlay) loadingOverlay.remove();
  } else {
    flyersContainer.innerHTML = `
      <div class="loading-overlay">
        <i class="fas fa-spinner fa-spin loading-spinner"></i>
        <p class="loading-text">Loading flyers...</p>
      </div>
    `;
  }

  // 2. BACKGROUND SYNC
  try {
    const generalFlyers = await fetchGeneralFlyers();
    const visibleGeneralFlyers = generalFlyers.filter((flyer) => flyer.IsVisible && flyer.IsActive);
    
    let tempAllFlyers = [...visibleGeneralFlyers];
    let tempMerchantFlyersMap = new Map();

    const uniqueMerchants = new Set();
    visibleGeneralFlyers.forEach((flyer) => {
      if (flyer.Merchant) uniqueMerchants.add(flyer.Merchant);
      if (!tempMerchantFlyersMap.has(flyer.Merchant)) tempMerchantFlyersMap.set(flyer.Merchant, []);
      tempMerchantFlyersMap.get(flyer.Merchant).push(flyer);
    });

    const merchantPromises = Array.from(uniqueMerchants).map(async (merchant) => {
      try {
        const merchantSpecificFlyers = await fetchMerchantFlyers(merchant);
        const visibleMerchantFlyers = merchantSpecificFlyers.filter(
          (flyer) => flyer.IsVisible && flyer.IsActive,
        );
        tempAllFlyers.push(...visibleMerchantFlyers);

        if (!tempMerchantFlyersMap.has(merchant)) tempMerchantFlyersMap.set(merchant, []);

        const existingUrls = new Set(tempMerchantFlyersMap.get(merchant).map((f) => f.FlyerImageUrl));
        visibleMerchantFlyers.forEach((flyer) => {
          if (!existingUrls.has(flyer.FlyerImageUrl)) {
            tempMerchantFlyersMap.get(merchant).push(flyer);
          }
        });
      } catch (error) {
        console.error(`Error loading flyers for ${merchant}:`, error);
      }
    });

    await Promise.all(merchantPromises);

    // 3. DIFF & SWAP
    const newCacheData = {
      allFlyers: tempAllFlyers,
      merchantMapEntries: Array.from(tempMerchantFlyersMap.entries()),
      visibleGeneralFlyers: visibleGeneralFlyers
    };

    if (JSON.stringify(newCacheData) !== JSON.stringify(cachedFlyers)) {
      AppState.allFlyers = tempAllFlyers;
      AppState.merchantFlyersMap = tempMerchantFlyersMap;
      
      await setCachedData(cacheKey, newCacheData);
      
      displayFlyers(visibleGeneralFlyers);
      setupCardImages();
      setupHeroMarquee();
    }

  } catch (error) {
    console.error("Error loading flyers:", error);
    if (!cachedFlyers) {
      flyersContainer.innerHTML = `
        <div style="grid-column: 1 / -1; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 50vh; width: 100%; text-align: center; color: #dc143c;">
          <i class="fas fa-exclamation-circle" style="font-size: 3.5rem; margin-bottom: 15px; opacity: 0.8;"></i>
          <h3 style="font-size: 1.5rem; margin-bottom: 10px;">Unable to load flyers</h3>
          <p style="font-size: 1.1rem; color: var(--gray-700);">Please try again later.</p>
        </div>
      `;
    }
  }
}

export function displayFlyers(flyers) {
  flyersContainer.innerHTML = "";
  flyers.forEach((flyer, index) => {
    const card = createFlyerCard(flyer, index);
    flyersContainer.appendChild(card);
  });

  if (flyers.length === 0) {
    flyersContainer.innerHTML = `
      <div style="grid-column: 1 / -1; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 50vh; width: 100%; text-align: center; color: var(--primary);">
        <i class="fas fa-folder-open" style="font-size: 3.5rem; margin-bottom: 15px; opacity: 0.8;"></i>
        <h3 style="font-size: 1.5rem; margin-bottom: 10px;">No flyers available</h3>
        <p style="color: var(--gray-700); font-size: 1.1rem;">Please check back later for new updates.</p>
      </div>
    `;
  }
}

export function createFlyerCard(flyer, index) {
  const card = document.createElement("div");
  card.className = "card";
  card.setAttribute("data-image", flyer.FlyerImageUrl);
  card.setAttribute("data-merchant", flyer.Merchant);

  const merchantName = formatMerchantName(flyer.Merchant);
  const expiryDate = flyer.ExpiryDate ? formatDate(flyer.ExpiryDate) : "N/A";
  const state = flyer.State || "N/A";
  const pageNo = flyer.PageNo || "1";
  const merchantFlyers = AppState.merchantFlyersMap.get(flyer.Merchant) || [];
  const totalPages = merchantFlyers.length;

  card.innerHTML = `
    <div class="card-header">
      <div class="merchant-header">${merchantName}</div>
      <div class="page-no">
        <i class="fas fa-file-alt"></i> ${pageNo} of ${totalPages}
      </div>
    </div>
    <div class="card-image"></div>
    <div class="card-info">
      <div class="info-row">
        <div class="info-item">
          <i class="fas fa-map-marker-alt info-icon"></i>
          <span class="state">${state}</span>
        </div>
      </div>
      <div class="info-row">
        <div class="info-item">
          <i class="fas fa-calendar-alt info-icon"></i>
          <span class="expiry-date">Valid until: ${expiryDate}</span>
        </div>
      </div>
    </div>
    <div class="card-label">
      <i class="fas fa-copy"></i> ${totalPages} Pages available
    </div>
  `;

  return card;
}

// ============================================
// DEALS FUNCTIONS
// ============================================

export function generateDealId(deal) {
  const merchant = deal.merchant || "";
  const product = deal.product_name || "";
  const price = deal.offer_price || "";
  const weight = deal.weight_quantity || "";
  const expiry = deal.offerEnd || "";
  return `${merchant}|${product}|${price}|${weight}|${expiry}`;
}

export async function loadDeals(filterRecent = false) {
  try {
    AppState.currentView = "deals";
    document.body.classList.add("collapse-top-sections");
    updateStickyHeadingForDeals();

    const userIdValue = (AppState.isLoggedIn && AppState.user_uid !== "guest") ? AppState.user_uid : (AppState.SESSION_ID || "defaultUser");
    const cacheKey = `deals_${userIdValue}`;

    // 1. INSTANT MOUNT
    const cachedDeals = await getCachedData(cacheKey);
    if (cachedDeals) {
      let displayDeals = cachedDeals;
      if (filterRecent) {
        const todayStr = new Date().toISOString().split("T")[0];
        displayDeals = cachedDeals.filter(d => d.createdDatetime && d.createdDatetime.split("T")[0] === todayStr);
      }
      displayDealsCards(displayDeals);
    } else {
      flyersContainer.innerHTML = `
        <div class="loading-overlay">
          <i class="fas fa-spinner fa-spin loading-spinner"></i>
          <p class="loading-text">Loading your deals...</p>
        </div>
      `;
    }

    // 2. BACKGROUND SYNC
    const url = `${API_BASE_URL}/productSearch?userId=${encodeURIComponent(userIdValue)}&sessionId=${encodeURIComponent(AppState.SESSION_ID || "")}`;
    const response = await fetch(url, { method: "GET", headers: { "Content-Type": "application/json" } });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    
    const dealsData = await response.json();
    let formattedDeals = dealsData.map(deal => {
      if (deal.markFavourite === undefined) deal.markFavourite = false;
      return deal;
    });

    // 3. DIFF & SWAP
    if (JSON.stringify(formattedDeals) !== JSON.stringify(cachedDeals)) {
      await setCachedData(cacheKey, formattedDeals);
      
      let finalDeals = formattedDeals;
      if (filterRecent) {
        const todayStr = new Date().toISOString().split("T")[0];
        finalDeals = formattedDeals.filter(d => d.createdDatetime && d.createdDatetime.split("T")[0] === todayStr);
      }
      displayDealsCards(finalDeals);
    }

  } catch (error) {
    console.error("❌ Error loading deals:", error);
    if (!cachedDeals) {
      flyersContainer.innerHTML = `
        <div style="text-align: center; padding: 40px 20px; color: #dc143c; width: 100%;">
          <i class="fas fa-wifi" style="font-size: 3rem; margin-bottom: 15px; opacity: 0.5;"></i>
          <h3 style="font-size: 1.5rem; margin-bottom: 10px; color: #333;">You're Offline</h3>
          <p style="color: #666;">Please check your internet connection to see your deals.</p>
        </div>
      `;
    }
  }
}

export async function loadWishlist() {
  try {
    AppState.currentView = "wishlist";
    document.body.classList.add("collapse-top-sections");
    updateStickyHeadingForWishlist();

    const fetchUid = (AppState.isLoggedIn && AppState.user_uid !== "guest") ? AppState.user_uid : "defaultUser";
    const cacheKey = `wishlist_${fetchUid}`;

    // 1. INSTANT MOUNT
    const cachedWishlist = await getCachedData(cacheKey);
    if (cachedWishlist) {
      renderWishlistUI(cachedWishlist);
    } else {
      flyersContainer.innerHTML = `
        <div class="loading-overlay">
          <i class="fas fa-spinner fa-spin loading-spinner"></i>
          <p class="loading-text">Loading your wishlist...</p>
        </div>
      `;
    }

    // 2. BACKGROUND SYNC
    const url = `${API_BASE_URL}/productSearch?userId=${encodeURIComponent(fetchUid)}&sessionId=${encodeURIComponent(AppState.SESSION_ID || "UsersFavoriteItems")}`;
    const response = await fetch(url, { method: "GET", headers: { "Content-Type": "application/json" } });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    
    const allDeals = await response.json();
    let favorites = allDeals.filter((deal) => deal.markFavourite === true);
    favorites.sort((a, b) => (a.merchant || "").toLowerCase().localeCompare((b.merchant || "").toLowerCase()));

    // 3. DIFF & SWAP
    if (JSON.stringify(favorites) !== JSON.stringify(cachedWishlist)) {
      await setCachedData(cacheKey, favorites);
      renderWishlistUI(favorites);
    }

  } catch (error) {
    console.error("❌ Error loading wishlist:", error);
    if (!cachedWishlist) {
      flyersContainer.innerHTML = `
        <div style="text-align: center; padding: 40px 20px; color: #dc143c; width: 100%;">
          <i class="fas fa-wifi" style="font-size: 3rem; margin-bottom: 15px; opacity: 0.5;"></i>
          <h3 style="font-size: 1.5rem; margin-bottom: 10px; color: #333;">You're Offline</h3>
          <p style="color: #666;">Please check your internet connection to view your Shopping Cart.</p>
        </div>
      `;
    }
  }
}

// Extract the UI rendering so we can call it twice
function renderWishlistUI(favorites) {
  if (favorites.length > 0) {
    const wishlistContainer = document.createElement("div");
    wishlistContainer.className = "wishlist-container";
    favorites.forEach((deal, index) => {
      const card = createDealCard(deal, index);
      wishlistContainer.appendChild(card);
    });
    flyersContainer.innerHTML = "";
    flyersContainer.appendChild(wishlistContainer);
  } else {
    flyersContainer.innerHTML = `
      <div class="empty-wishlist">
        <i class="far fa-heart"></i>
        <h3>Your wishlist is empty</h3>
        <p>Browse deals and click the bag icon to add items to your wishlist.</p>
        <button class="back-to-flyers-btn"><i class="fas fa-arrow-left"></i> Back to Flyers</button>
      </div>
    `;
    const backBtn = document.querySelector(".back-to-flyers-btn");
    if (backBtn) backBtn.addEventListener("click", async () => await loadFlyersView());
  }
}

export function updateStickyHeadingForWishlist() {
  stickyHeading.innerHTML = "";
  const backButton = document.createElement("button");
  backButton.className = "back-button";
  backButton.innerHTML = '<i class="fas fa-arrow-left"></i>';
  backButton.title = "Back to Flyers";
  backButton.addEventListener("click", async (e) => {
    e.stopPropagation();

    navOptions.forEach((opt) => {
      opt.style.background = "rgba(255, 255, 255, 0.7)";
      opt.style.color = "#444";
      opt.style.boxShadow = "none";
    });

    await loadFlyersView();
  });

  const headingText = document.createElement("span");
  headingText.className = "heading-text";
  headingText.innerHTML = "Shopping Cart";

  stickyHeading.appendChild(backButton);
  stickyHeading.appendChild(headingText);

  const searchContainer = stickyHeading.querySelector(
    ".deals-search-container",
  );
  if (searchContainer) {
    searchContainer.remove();
  }
}

export function updateStickyHeadingForDeals() {
  stickyHeading.innerHTML = "";
  const backButton = document.createElement("button");
  backButton.className = "back-button";
  backButton.innerHTML = '<i class="fas fa-arrow-left"></i>';
  backButton.title = "Back to Flyers";
  backButton.addEventListener("click", async (e) => {
    e.stopPropagation();

    navOptions.forEach((opt) => {
      if (opt.textContent.includes("Deals")) {
        opt.style.background = "rgba(255, 255, 255, 0.7)";
        opt.style.color = "#444";
        opt.style.boxShadow = "none";
      }
    });

    await loadFlyersView();
  });

  const headingText = document.createElement("span");
  headingText.className = "heading-text";
  headingText.innerHTML = '<i class="fas fa-chart-bar"></i> Deals';

  stickyHeading.appendChild(backButton);
  stickyHeading.appendChild(headingText);
  addDealsSearchBox();
}

export function updateStickyHeadingForFlyers() {
  const postal = getCurrentPostalCode();

  stickyHeading.innerHTML = `
    <i class="fas fa-chart-bar"></i> Flyers in 
    <button id="flyersLocationBtn" style="background:none;border:none;padding:0;font:inherit;font-weight:700;color:var(--primary);cursor:pointer;display:inline-flex;align-items:center;gap:6px;">
      <span id="flyersPostalCode" style="text-decoration:underline;">${postal}</span>
      <i class="fas fa-pen" style="font-size:0.75em; opacity:0.8; text-decoration:none;"></i>
    </button>
  `;

  const searchContainer = stickyHeading.querySelector(".deals-search-container");
  if (searchContainer) {
    searchContainer.remove();
  }

  const locationBtn = stickyHeading.querySelector("#flyersLocationBtn");
  if (locationBtn) {
    locationBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      openFlyersLocationModal();
    });
  }
}

function getCurrentPostalCode() {
  try {
    const saved = JSON.parse(localStorage.getItem("walleto_location") || "{}");
    return saved.postal_code || saved.postalCode || "L9T 1A1";
  } catch (e) {
    return "L9T 1A1";
  }
}

function openFlyersLocationModal() {
  const modal = document.getElementById("manualLocationModal");
  const input = document.getElementById("manualLocationInput");
  if (input) input.value = "";
  if (modal) modal.classList.add("active");
  setTimeout(() => { if (input) input.focus(); }, 100);
}

export async function loadFlyersView() {
  AppState.merchantFlyersMap = new Map(); 
  
  const flyerContainer = document.getElementById("flyer-container"); 
  if (flyerContainer) {
    flyerContainer.innerHTML = ""; 
  }

  AppState.currentView = "flyers";
  document.body.classList.remove("collapse-top-sections");
  updateStickyHeadingForFlyers();
  navOptions.forEach((opt) => {
    opt.style.background = "rgba(255, 255, 255, 0.7)";
    opt.style.color = "#444";
    opt.style.boxShadow = "none";
  });

  await loadFlyers();
}

export function displayDealsCards(dealsData) {
  flyersContainer.innerHTML = "";
  window.dealsData = dealsData || [];

  const dealsContainer = document.createElement("div");
  dealsContainer.className = "deals-container";
  dealsContainer.id = "dealsContainer";

  if (window.dealsData && window.dealsData.length > 0) {
    window.dealsData.forEach((deal, index) => {
      const card = createDealCard(deal, index);
      dealsContainer.appendChild(card);
    });
  } else {
    const emptyState = document.createElement("div");
    emptyState.className = "empty-deals";
    emptyState.innerHTML = `
      <i class="fas fa-info-circle"></i>
      <h3>No deals available</h3>
      <p>Check back later for new deals from your favorite merchants!</p>
    `;
    dealsContainer.appendChild(emptyState);
  }

  flyersContainer.appendChild(dealsContainer);
}

export function createDealCard(deal, index) {
  const card = document.createElement("div");
  card.className = "deal-card";
  card.setAttribute("data-index", index);
  const dealId = generateDealId(deal);

  const isFavorited = deal.markFavourite === true;

  const offerDate = deal.offerEnd ? formatDate(deal.offerEnd) : "N/A";

  card.innerHTML = `
    <div class="deal-image-container">
      ${deal.crop_ImagePath && deal.crop_ImagePath.startsWith("http")
      ? `<img src="${deal.crop_ImagePath}" class="deal-image" alt="${deal.product_name || "Product"}">`
      : `<div class="no-deal-image"><i class="fas fa-image"></i></div>`
    }
    </div>
    <div class="deal-content">
      <div class="deal-merchant">${deal.merchant || "N/A"}</div>
      <div class="deal-product-name">${deal.product_name || "N/A"}</div>
        <div class="deal-price-section">
          <div class="price-weight-row">
            <div class="price-column">
              <div class="offer-price">${deal.offer_price || "N/A"}</div>
              ${deal.regular_price ? `<div class="regular-price">${deal.regular_price}</div>` : ""}
            </div>
            ${deal.weight_quantity ? `<div class="deal-weight">${deal.weight_quantity}</div>` : ""}
          </div>
        </div>
        <div class="deal-expiry">
          <i class="fas fa-calendar-alt"></i>
          <span>Valid until: ${offerDate}</span>
        </div>
    </div>
    <button class="deal-favorite-btn ${isFavorited ? "favorited" : ""}">
      <i class="fa-solid fa-bag-shopping"></i>
    </button>
  `;

  card.dealData = deal;
  const favoriteBtn = card.querySelector(".deal-favorite-btn");
  favoriteBtn.addEventListener("click", function (e) {
    e.stopPropagation();
    toggleFavorite(deal, index, card);
  });

  return card;
}

export function showDealModal(deal, card) {
  AppState.currentDealCard = card;
  if (card) {
    card.dealData = deal; 
  }

  dealModalImage.src =
    deal.crop_ImagePath && deal.crop_ImagePath.startsWith("http")
      ? deal.crop_ImagePath
      : "";
  dealModalMerchant.textContent = deal.merchant || "N/A";
  dealModalProduct.textContent = deal.product_name || "N/A";
  dealModalOfferPrice.innerHTML = `Offer Price: ${deal.offer_price || "N/A"}`;
  dealModalRegularPrice.innerHTML = deal.regular_price ? `Regular: ${deal.regular_price}` : "";
  dealModalWeight.innerHTML = `Qty: ${deal.weight_quantity || "N/A"}`;
  dealModalExpiryDate.textContent = `Expires: ${deal.offerEnd ? formatDate(deal.offerEnd) : "N/A"}`;
  dealModalPageNo.textContent = `FlyerpageNo: ${deal.flyerPageNo || deal.page_no || "N/A"}`;

  const merchant = deal.merchant;
  const pageNo = deal.flyerPageNo || deal.page_no;
  const flyerImg = document.getElementById("dealModalFlyerImage");
  const flyerWrapper = document.querySelector(".flyer-image-wrapper");
  const dealWrapper = document.querySelector(".deal-image-wrapper");

  let flyerImageUrl = "";
  if (
    merchant &&
    pageNo &&
    AppState.merchantFlyersMap &&
    AppState.merchantFlyersMap.has(merchant)
  ) {
    const flyers = AppState.merchantFlyersMap.get(merchant);
    const flyer = flyers.find((f) => f.PageNo == pageNo);
    if (flyer) {
      flyerImageUrl = flyer.FlyerImageUrl;
    }
  }

  if (flyerImageUrl) {
    flyerImg.src = flyerImageUrl;
    flyerWrapper.style.display = "block";
    flyerWrapper.style.opacity = "0.5";
    dealWrapper.classList.remove("full-width");
    dealWrapper.style.animation = "none";
    dealWrapper.offsetHeight;
    dealWrapper.style.animation = "slideInRight 1s ease-out";
  } else {
    flyerImg.src = "";
    flyerWrapper.style.display = "none";
    dealWrapper.classList.add("full-width");
    dealWrapper.style.animation = "none";
  }

  updateDealModalFavoriteButton(deal);
  dealModal.classList.add("active");
  document.body.style.overflow = "hidden";
}

export function closeDealModal() {
  dealModal.classList.remove("active");
  document.body.style.overflow = "";
  AppState.currentDealCard = null;

  const flyerImg = document.getElementById("dealModalFlyerImage");
  const dealImg = document.getElementById("dealModalImage");
  const flyerWrapper = document.querySelector(".flyer-image-wrapper");
  const dealWrapper = document.querySelector(".deal-image-wrapper");

  if (flyerImg) flyerImg.src = "";
  if (dealImg) dealImg.src = "";
  if (flyerWrapper) flyerWrapper.style.display = "block";
  if (dealWrapper) dealWrapper.classList.remove("full-width");
}

export function updateDealModalFavoriteButton(deal) {
  const isFavorited = deal.markFavourite === true;

  dealModalFavoriteBtn.innerHTML = isFavorited
    ? '<i class="fa-solid fa-bag-shopping"></i> Remove from Shopping Cart'
    : '<i class="fa-solid fa-bag-shopping"></i> Add to Shopping Cart';

  if (isFavorited) {
    dealModalFavoriteBtn.classList.add("favorited");
  } else {
    dealModalFavoriteBtn.classList.remove("favorited");
  }
}

export async function toggleFavoriteOnServer(deal, isFavorite) {
  // Use user's UID if logged in; otherwise fall back to defaultUser
  const effectiveUid = (AppState.isLoggedIn && AppState.user_uid !== "guest")
    ? AppState.user_uid
    : "defaultUser";

  try {
    // 1. Convert the payload into query parameters
    const params = new URLSearchParams({
      uid: effectiveUid,
      sessionId: AppState.SESSION_ID || "UsersFavoriteItems",
      isFavorite: isFavorite,
      crop_Image: deal.crop_ImagePath || deal.crop_Image || "",
      productName: deal.product_name || "",
    });

    // 2. Append the params to the URL and remove the body
    const response = await fetch(`${API_BASE_URL}/toggleFavorite?${params.toString()}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      }
      // body: JSON.stringify(payload) <-- REMOVED
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server error: ${response.status} - ${errorText}`);
    }

    return true;
  } catch (error) {
    console.error("Failed to toggle favorite on server:", error);
    return false;
  }
}

export async function toggleFavorite(deal, index, card) {
  // 1. Read the current state directly from the deal data memory (avoids the crash)
  const isCurrentlyFavorited = deal.markFavourite === true;
  const newState = !isCurrentlyFavorited;

  // 2. Instantly update the memory data
  deal.markFavourite = newState;
  if (index >= 0 && window.dealsData && window.dealsData[index]) {
    window.dealsData[index].markFavourite = newState;
  }

  // 3. Safely update the tiny card button ONLY if it actually exists on the card
  if (card) {
    const favoriteBtn = card.querySelector(".deal-favorite-btn");
    if (favoriteBtn) {
      if (newState) {
        favoriteBtn.classList.add("favorited");
      } else {
        favoriteBtn.classList.remove("favorited");
      }
    }
  }

  // 4. Wishlist page UI logic (remove the card instantly if removed from wishlist)
  if (AppState.currentView === "wishlist" && !newState && card) {
    const wishlistContainer = document.querySelector(".wishlist-container");
    if (wishlistContainer && wishlistContainer.contains(card)) {
      card.remove();

      if (wishlistContainer.children.length === 0) {
        flyersContainer.innerHTML = `
          <div class="empty-wishlist">
            <i class="far fa-heart"></i>
            <h3>Your wishlist is empty</h3>
            <p>Browse deals and click the bag icon to add items to your wishlist.</p>
            <button class="back-to-flyers-btn">
                <i class="fas fa-arrow-left"></i>
                Back to Flyers
            </button>
          </div>
        `;
        const backBtn = document.querySelector(".back-to-flyers-btn");
        if (backBtn) {
          backBtn.addEventListener("click", async () => {
            await loadFlyersView();
          });
        }
      }
    }
  }

  // 5. Make the API Call - The function will no longer crash before reaching this step
  const success = await toggleFavoriteOnServer(deal, newState);
  
  if (!success) {
    console.error("Server toggle failed, reverting local state");

    // Revert the memory data
    deal.markFavourite = isCurrentlyFavorited;
    if (index >= 0 && window.dealsData && window.dealsData[index]) {
      window.dealsData[index].markFavourite = isCurrentlyFavorited;
    }
    
    // Revert the tiny card button if it exists
    if (card) {
      const favoriteBtn = card.querySelector(".deal-favorite-btn");
      if (favoriteBtn) {
        if (isCurrentlyFavorited) {
          favoriteBtn.classList.add("favorited");
        } else {
          favoriteBtn.classList.remove("favorited");
        }
      }
    }

    if (AppState.currentView === "wishlist") {
      await loadWishlist();
    }
  }
}

// ============================================
// SEARCH FUNCTIONALITY FOR DEALS
// ============================================

export function normalizeText(text) {
  if (!text) return "";

  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function tokenizeText(text) {
  const normalized = normalizeText(text);
  return normalized.split(/\s+/).filter((token) => token.length > 1);
}

export function generateNgrams(tokens, maxN = 3) {
  const ngrams = new Set();

  for (let n = 1; n <= maxN; n++) {
    for (let i = 0; i <= tokens.length - n; i++) {
      const ngram = tokens.slice(i, i + n).join(" ");
      ngrams.add(ngram);
    }
  }

  return Array.from(ngrams);
}

export function calculateSimilarityScore(queryTokens, productTokens, productName) {
  if (queryTokens.length === 0 || productTokens.length === 0) return 0;

  let score = 0;
  const querySet = new Set(queryTokens);
  const productSet = new Set(productTokens);

  for (const token of queryTokens) {
    if (productSet.has(token)) {
      score += 15;
    }
  }

  for (const queryToken of queryTokens) {
    if (queryToken.length < 3) continue;

    for (const productToken of productTokens) {
      if (productToken.includes(queryToken)) {
        score += 8;
      }
    }
  }

  const queryNgrams = generateNgrams(
    queryTokens,
    Math.min(3, queryTokens.length),
  );
  const productNgrams = generateNgrams(
    productTokens,
    Math.min(3, productTokens.length),
  );

  for (const ngram of queryNgrams) {
    if (productNgrams.includes(ngram)) {
      score += ngram.split(" ").length * 5;
    }
  }

  const queryPhrase = queryTokens.join(" ");
  if (productName.toLowerCase().includes(queryPhrase.toLowerCase())) {
    score += 30; // Big bonus for exact phrase match
  }

  for (const queryToken of queryTokens) {
    for (const productToken of productTokens) {
      if (productToken.startsWith(queryToken) && queryToken.length >= 3) {
        score += 6;
      }
    }
  }

  if (queryTokens.length === 1 && queryTokens[0].length < 4) {
    score = Math.max(0, score - 5);
  }

  return score;
}

export function setupSearchSuggestions() {
  const searchInput = document.querySelector(".deals-search-input");
  if (!searchInput) return;

  let suggestionsContainer = document.querySelector(".search-suggestions");

  if (!suggestionsContainer) {
    suggestionsContainer = document.createElement("div");
    suggestionsContainer.className = "search-suggestions";
    searchInput.parentNode.appendChild(suggestionsContainer);
  }

  searchInput.addEventListener(
    "input",
    debounce(async function (e) {
      const searchTerm = e.target.value.trim();

      if (searchTerm.length < 2) {
        suggestionsContainer.style.display = "none";
        return;
      }
      const uniqueProductNames = Array.from(
        new Set(
          (window.dealsData || [])
            .map((deal) => deal.product_name)
            .filter(
              (name) =>
                name && name.toLowerCase().includes(searchTerm.toLowerCase()),
            ),
        ),
      ).slice(0, 5);

      if (uniqueProductNames.length > 0) {
        suggestionsContainer.innerHTML = uniqueProductNames
          .map((name) => `<div class="suggestion-item">${name}</div>`)
          .join("");
        suggestionsContainer.style.display = "block";
      } else {
        suggestionsContainer.style.display = "none";
      }
    }, 300),
  );
  suggestionsContainer.addEventListener("click", function (e) {
    if (e.target.classList.contains("suggestion-item")) {
      searchInput.value = e.target.textContent;
      suggestionsContainer.style.display = "none";
      searchInput.focus();
      filterDealsCards(e.target.textContent);
    }
  });
  document.addEventListener("click", function (e) {
    if (
      !searchInput.contains(e.target) &&
      !suggestionsContainer.contains(e.target)
    ) {
      suggestionsContainer.style.display = "none";
    }
  });
}

export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function addDealsSearchBox() {
  if (AppState.currentView !== "deals") return;

  const existingSearch = stickyHeading.querySelector(".deals-search-container");
  if (existingSearch) {
    existingSearch.remove();
  }

  const searchContainer = document.createElement("div");
  searchContainer.className = "deals-search-container";

  const searchInput = document.createElement("input");
  searchInput.type = "text";
  searchInput.placeholder = "Search products by name...";
  searchInput.className = "deals-search-input";

  const searchIcon = document.createElement("i");
  searchIcon.className = "fas fa-search search-icon";

  searchContainer.appendChild(searchIcon);
  searchContainer.appendChild(searchInput);

  const headingText = stickyHeading.querySelector(".heading-text");
  if (headingText) {
    stickyHeading.insertBefore(searchContainer, headingText.nextSibling);
  } else {
    stickyHeading.appendChild(searchContainer);
  }
  searchInput.addEventListener("input", function (e) {
    filterDealsCards(e.target.value);
  });
  setupSearchSuggestions();
}

export function getMatchLevel(score) {
  if (score >= 40) return "high";
  if (score >= 20) return "mid";
  if (score > 0) return "low";
  return "none";
}

export function filterDealsCards(searchTerm) {
  const dealsContainer = document.getElementById("dealsContainer");
  if (!dealsContainer) return;

  const cards = dealsContainer.querySelectorAll(".deal-card");
  if (cards.length === 0) return;

  if (!searchTerm.trim()) {
    cards.forEach((card) => {
      card.style.display = "flex";
      card.classList.remove("high-match", "mid-match", "low-match");
    });
    return;
  }

  const queryTokens = tokenizeText(searchTerm);
  let visibleCards = [];
  let matchScores = [];

  cards.forEach((card) => {
    const productName = card.querySelector(".deal-product-name").textContent;
    const productTokens = tokenizeText(productName);
    const score = calculateSimilarityScore(
      queryTokens,
      productTokens,
      productName,
    );
    const matchLevel = getMatchLevel(score);
    card.classList.remove("high-match", "mid-match", "low-match");

    if (score > 0) {
      card.style.display = "flex";
      visibleCards.push(card);
      matchScores.push({ card, score, matchLevel });
      if (matchLevel === "high") {
        card.classList.add("high-match");
      } else if (matchLevel === "mid") {
        card.classList.add("mid-match");
      } else if (matchLevel === "low") {
        card.classList.add("low-match");
      }
    } else {
      card.style.display = "none";
    }
  });
  matchScores.sort((a, b) => b.score - a.score);
  matchScores.forEach((item, index) => {
    dealsContainer.appendChild(item.card);
    item.card.setAttribute("data-rank", index + 1);
  });
}

export function extractPriceValue(priceText) {
  if (!priceText || priceText === "N/A") return Infinity;
  const match = priceText.match(/(\d+\.?\d*)/);
  if (match) {
    return parseFloat(match[1]);
  }

  return Infinity;
}

// ============================================
// HOMEPAGE DEALS IMAGES (Stale-While-Revalidate)
// ============================================
let heroImageInterval = null;

export async function loadHomepageDealImages() {
  const fullSection = document.getElementById("homepageDealsContainer");
  const wrapper = document.getElementById("walletStackWrapper");
  if (!wrapper || !fullSection) return;

  // 1. INSTANT MOUNT: Pull from IndexedDB and paint immediately
  const cachedImages = await getCachedData("hottest_deals");
  if (cachedImages) {
    renderHomepageDeals(cachedImages, fullSection);
  }

  // 2. BACKGROUND SYNC: Fetch fresh data quietly
  try {
    const response = await fetch(`${API_BASE_URL}/homepageDealImages`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const responseData = await response.json();

    let imagesList = responseData.data || [];
    imagesList = imagesList.filter((item) => {
      return (item.Country === AppState.selectedCountryName && item.IsVisible === true && item.IsActive === true);
    });
    imagesList.sort((a, b) => (a.Priority || 99) - (b.Priority || 99));

    while (imagesList.length > 0 && imagesList.length < 3) {
      imagesList = imagesList.concat(imagesList);
    }

    // 3. DIFF & SWAP: Only update the UI and Cache if the backend data actually changed
    if (JSON.stringify(imagesList) !== JSON.stringify(cachedImages)) {
      await setCachedData("hottest_deals", imagesList);
      renderHomepageDeals(imagesList, fullSection); // Re-render with new data
    }

  } catch (error) {
    console.error("❌ Error loading homepage deal images:", error);
    if (!cachedImages) fullSection.style.display = "none";
  }
}

// Extract the exact UI logic you already had into this helper function
function renderHomepageDeals(imagesList, fullSection) {
  if (imagesList.length >= 3) {
    fullSection.style.display = "block";
    let cards = [
      document.getElementById("walletCard1"),
      document.getElementById("walletCard2"),
      document.getElementById("walletCard3")
    ];

    const updateCardData = (cardElement, dataItem) => {
      const img = cardElement.querySelector("img");
      img.src = dataItem.ProductImagePath;
      img.alt = dataItem.CompanyName || "Deal Image";
      if (dataItem.CompanyName) cardElement.setAttribute("data-merchant", dataItem.CompanyName);
    };

    updateCardData(cards[0], imagesList[0]);
    updateCardData(cards[1], imagesList[1]);
    updateCardData(cards[2], imagesList[2]);

    let nextImgIndex = 3 % imagesList.length;

    cards.forEach(card => {
      card.onclick = function () {
        const merchantName = this.getAttribute("data-merchant");
        if (merchantName) {
          if (AppState.merchantFlyersMap && AppState.merchantFlyersMap.has(merchantName)) {
            showFlyerGallery(merchantName);
          } else {
            const formattedName = formatMerchantName(merchantName);
            if (AppState.merchantFlyersMap && AppState.merchantFlyersMap.has(formattedName)) {
              showFlyerGallery(formattedName);
            } else {
              alert(`No flyers currently available for ${merchantName}`);
            }
          }
        }
      };
    });

    if (heroImageInterval) clearInterval(heroImageInterval);
    
    heroImageInterval = setInterval(() => {
      const frontCard = cards[0];
      const midCard = cards[1];
      const backCard = cards[2];

      frontCard.classList.remove("card-pos-1");
      frontCard.classList.add("card-out");
      midCard.classList.remove("card-pos-2");
      midCard.classList.add("card-pos-1");
      backCard.classList.remove("card-pos-3");
      backCard.classList.add("card-pos-2");

      setTimeout(() => {
        updateCardData(frontCard, imagesList[nextImgIndex]);
        nextImgIndex = (nextImgIndex + 1) % imagesList.length;

        frontCard.classList.remove("card-out");
        frontCard.classList.add("card-in");
        void frontCard.offsetWidth;
        frontCard.classList.remove("card-in");
        frontCard.classList.add("card-pos-3");

        cards.push(cards.shift());
      }, 600);
    }, 4000);
  } else {
    fullSection.style.display = "none";
  }
}

// ============================================
// USER FAVOURITES (Stale-While-Revalidate)
// ============================================
export async function loadUserFavourites() {
  const favSection = document.getElementById("userFavSection");
  const favScroll = document.getElementById("userFavsScroll");
  if (!favSection || !favScroll) return;

  const fetchUid = (AppState.isLoggedIn && AppState.user_uid !== "guest") 
    ? AppState.user_uid : "defaultUser";
  
  const cacheKey = `user_favs_${fetchUid}`;

  // 1. INSTANT MOUNT
  const cachedFavs = await getCachedData(cacheKey);
  if (cachedFavs) {
    renderUserFavourites(cachedFavs, favSection, favScroll);
  }

  // 2. BACKGROUND SYNC
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/favourites/${fetchUid}?active_only=true&limit=15`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) throw new Error(`API error! status: ${response.status}`);
    const favouritesData = await response.json();

    // 3. DIFF & SWAP
    if (JSON.stringify(favouritesData) !== JSON.stringify(cachedFavs)) {
      await setCachedData(cacheKey, favouritesData);
      renderUserFavourites(favouritesData, favSection, favScroll);
    }

  } catch (error) {
    console.error("❌ Error loading user favourites:", error);
    if (!cachedFavs) favSection.style.display = "none";
  }
}

// Helper to build the DOM (copied exactly from your original loop)
function renderUserFavourites(favouritesData, favSection, favScroll) {
  favScroll.innerHTML = "";
  if (favouritesData && favouritesData.length > 0) {
    favSection.style.display = "block";

    favouritesData.forEach((item) => {
      const products = [
        { img: item.product1_image, name: item.product1_name },
        { img: item.product2_image, name: item.product2_name },
        { img: item.product3_image, name: item.product3_name }
      ];

      products.forEach((prod) => {
        if (prod.img && prod.img.trim() !== "") {
          const merchantCard = document.createElement("div");
          merchantCard.className = "fav-merchant-card";
          
          if (item.merchant) {
            merchantCard.setAttribute("data-merchant", item.merchant);
            
            merchantCard.addEventListener("click", async (e) => {
              e.stopPropagation();
              
              const cleanStr = (str) => (str || "").toLowerCase().replace(/['"’\s]/g, ""); 
              const searchUserId = (AppState.isLoggedIn && AppState.user_uid !== "guest") 
                ? AppState.user_uid 
                : "defaultUser";
              const searchSessionId = "UsersFavoriteItems";
              
              const placeholderDeal = {
                merchant: item.merchant,
                product_name: prod.name,
                crop_ImagePath: prod.img,
                offer_price: '<span class="shimmer-text" style="width: 80px;"></span>',
                regular_price: "",
                weight_quantity: '<span class="shimmer-text" style="width: 60px;"></span>',
                offerEnd: null,
                flyerPageNo: "...",
                markFavourite: true
              };

              // 2. Open the modal IMMEDIATELY for a fast, responsive UI
              showDealModal(placeholderDeal, merchantCard);
              
              try {
                // 3. Fetch the rich data in the background
                let matchedDeal = null;
                if (window.firebase) {
                  const db = window.firebase.firestore();
                  const snapshot = await db.collection("userDealData")
                    .where("uid", "==", searchUserId)
                    .where("sessionId", "==", searchSessionId)
                    .where("merchant", "==", item.merchant)
                    .get();
                  
                  snapshot.forEach((doc) => {
                    const d = doc.data();
                    const isImageMatch = d.crop_ImagePath === prod.img || d.crop_Image === prod.img;
                    const isNameMatch = cleanStr(d.product_name) === cleanStr(prod.name);
                    
                    if (isImageMatch || isNameMatch) {
                      matchedDeal = d;
                    }
                  });
                }
                
                // 4. Update the ALREADY OPEN modal with the real data
                if (matchedDeal) {
                  matchedDeal.markFavourite = true; 
                  merchantCard.dealData = matchedDeal;
                  
                  // 5. FORCE DATABASE UPDATE: Fire the API in the background so the backend adds the missing fields
                  toggleFavoriteOnServer(matchedDeal, true);
                  
                  // Inject the fetched data into the open DOM elements
                  const offerPriceEl = document.getElementById("dealModalOfferPrice");
                  if (offerPriceEl) offerPriceEl.textContent = `Offer Price: ${matchedDeal.offer_price || "N/A"}`;
                  
                  const regularPriceEl = document.getElementById("dealModalRegularPrice");
                  if (regularPriceEl) regularPriceEl.textContent = matchedDeal.regular_price ? `Regular: ${matchedDeal.regular_price}` : "";
                  
                  const weightEl = document.getElementById("dealModalWeight");
                  if (weightEl) weightEl.textContent = `Qty: ${matchedDeal.weight_quantity || "N/A"}`;
                  
                  const expiryEl = document.getElementById("dealModalExpiryDate");
                  if (expiryEl) expiryEl.textContent = `Expires: ${matchedDeal.offerEnd ? formatDate(matchedDeal.offerEnd) : "N/A"}`;
                  
                  const pageNoEl = document.getElementById("dealModalPageNo");
                  if (pageNoEl) pageNoEl.textContent = `FlyerpageNo: ${matchedDeal.flyerPageNo || matchedDeal.page_no || "N/A"}`;

                  // Update the background flyer image dynamically if available
                  const flyerImg = document.getElementById("dealModalFlyerImage");
                  const flyerWrapper = document.querySelector(".flyer-image-wrapper");
                  const dealWrapper = document.querySelector(".deal-image-wrapper");
                  const pageNo = matchedDeal.flyerPageNo || matchedDeal.page_no;
                  let flyerImageUrl = "";

                  if (item.merchant && pageNo && AppState.merchantFlyersMap && AppState.merchantFlyersMap.has(item.merchant)) {
                    const flyers = AppState.merchantFlyersMap.get(item.merchant);
                    const flyer = flyers.find((f) => f.PageNo == pageNo);
                    if (flyer) flyerImageUrl = flyer.FlyerImageUrl;
                  }

                  if (flyerImageUrl && flyerImg) {
                    flyerImg.src = flyerImageUrl;
                    if (flyerWrapper) { flyerWrapper.style.display = "block"; flyerWrapper.style.opacity = "0.5"; }
                    if (dealWrapper) { dealWrapper.classList.remove("full-width"); dealWrapper.style.animation = "slideInRight 1s ease-out"; }
                  }
                } else {
                  // Fallback if data is missing
                  document.getElementById("dealModalOfferPrice").textContent = `Offer Price: N/A`;
                  document.getElementById("dealModalWeight").textContent = `Qty: N/A`;
                }
              } catch (error) {
                console.error("Error querying Firestore directly:", error);
                document.getElementById("dealModalOfferPrice").textContent = `Offer Price: Error`;
                document.getElementById("dealModalWeight").textContent = `Qty: Error`;
              }
            });
          }

          if (item.merchant_bg_image) {
            merchantCard.style.backgroundImage = `url('${item.merchant_bg_image_new}')`;
          }

          const overlay = document.createElement("div");
          overlay.className = "fav-products-overlay";

          const prodImg = document.createElement("img");
          prodImg.src = prod.img;
          prodImg.className = "fav-product-img";
          prodImg.alt = prod.name || "Product";
          prodImg.onerror = function () { merchantCard.style.display = "none"; };
          overlay.appendChild(prodImg);

          const infoContainer = document.createElement("div");
          infoContainer.className = "fav-info-container";

          if (prod.name && prod.name.trim() !== "") {
            const nameDiv = document.createElement("div");
            nameDiv.className = "fav-product-name";
            nameDiv.textContent = prod.name;
            infoContainer.appendChild(nameDiv);
          }

          overlay.appendChild(infoContainer);
          merchantCard.appendChild(overlay);
          favScroll.appendChild(merchantCard);
        }
      });
    });

    setTimeout(() => {
      const cards = favScroll.querySelectorAll(".fav-merchant-card");
      cards.forEach((card) => {
        const clone = card.cloneNode(true);
        favScroll.appendChild(clone);
      });
    }, 100);

  } else {
    favSection.style.display = "none";
  }
}